<template>
    <div>
        <fullscreen-loading v-if="loading && !activeEvent" />

        <div v-if="videoSrc" v-once>
            <video-player :src="{
                src: videoSrc
            }" />
        </div>

        <div v-if="activeEvent">
            <div class="d-flex mt-1">
                <span class="badge mr-2" :class="getPlayStateBadgeClass(activeEvent.play_state)">
                    {{ getPlayStateText(activeEvent.play_state) }}
                </span>
                <span>{{ $t('livestream.time_range', {
                    start: formatDateTime(activeEvent.startdate, 'HH:mm'),
                    end: formatDateTime(activeEvent.enddate, 'HH:mm'),
                }) }}</span>
            </div>

            <div class="mt-5">
                <div v-if="activeEvent.play_state === LIVESTREAM_PLAY_STATES.WAIT_FOR_MANUAL_START" class="d-flex align-items-center">
                    <button class="w-100 btn btn-xs btn-success" @click="startLivestream">
                        {{ $t('livestream.start_button') }}
                    </button>
                </div>

                <div v-if="activeEvent.play_state === LIVESTREAM_PLAY_STATES.LIVE" class="d-flex align-items-center">
                    <button class="w-100 btn btn-xs btn-danger" @click="stopLivestream">
                        {{ $t('livestream.stop_button') }}
                    </button>
                    <button class="w-100 btn btn-xs btn-danger" @click="stopLivestream(true)">
                        {{ $t('livestream.stop_with_message_button') }}
                    </button>
                </div>
            </div>
        </div>

        <div v-else>
            <div class="alert alert-info" v-if="!loading">
                <p>{{ $t('livestream.no_active_stream') }}</p>
            </div>
        </div>
    </div>
</template>


<script>

    import VideoPlayer from './VideoPlayer.vue';
    import FullscreenLoading from './FullscreenLoading.vue';
    import { LIVESTREAM_PLAY_STATES } from '../enums/livestream';
    import UtilMixin from '../designer/mixins/UtilMixin.vue';

    export default {
        mixins: [
            UtilMixin,
        ],

        props: {
            ceremony: {
                type: Object,
                required: true,
            },
        },

        components: {
            FullscreenLoading,
            VideoPlayer,
        },

        data() {
            return {
                LIVESTREAM_PLAY_STATES,

                loading: false,
                activeEvent: null,
                videoSrc: null,

                streamingStatusIntervalId: null,
            };
        },

        async mounted() {
            await this.loadStreamingStatus();

            this.streamingStatusIntervalId = setInterval(() => {
                this.loadStreamingStatus();
            }, 10000);
        },

        methods: {
            getPlayStateBadgeClass(playState) {
                switch (playState) {
                    case LIVESTREAM_PLAY_STATES.LIVE:
                        return 'badge-success';
                    case LIVESTREAM_PLAY_STATES.LIVE_NEARLY_ENDING:
                        return 'badge-primary';
                    case LIVESTREAM_PLAY_STATES.VOD:
                        return 'badge-info';
                    case LIVESTREAM_PLAY_STATES.WAITING_FOR_VOD:
                    case LIVESTREAM_PLAY_STATES.NO_VOD:
                        return 'badge-warning';
                    case LIVESTREAM_PLAY_STATES.COUNTER:
                        return 'badge-primary';
                    case LIVESTREAM_PLAY_STATES.WAIT_FOR_MANUAL_START:
                        return 'badge-warning';
                }

                return 'badge-info';
            },

            getPlayStateText(playState) {
                switch (playState) {
                    case LIVESTREAM_PLAY_STATES.LIVE:
                        return this.$t('livestream.play_state.active');
                    case LIVESTREAM_PLAY_STATES.LIVE_NEARLY_ENDING:
                        return this.$t('livestream.play_state.nearly_ending');
                    case LIVESTREAM_PLAY_STATES.VOD:
                        return this.$t('livestream.play_state.ended');
                    case LIVESTREAM_PLAY_STATES.WAITING_FOR_VOD:
                        return this.$t('livestream.play_state.no_vod');
                    case LIVESTREAM_PLAY_STATES.NO_VOD:
                        return this.$t('livestream.play_state.no_vod');
                    case LIVESTREAM_PLAY_STATES.COUNTER:
                        return this.$t('livestream.play_state.counter');
                    case LIVESTREAM_PLAY_STATES.WAIT_FOR_MANUAL_START:
                        return this.$t('livestream.play_state.wait_for_manual_start');
                }

                return this.$t('ceremony.type_unknown');
            },

            async loadStreamingStatus() {
                this.loading = true;

                try {
                    const response = await this.$http.get(`/api/player/ceremonies/${this.ceremony.id}/livestream/status`);

                    const eventChanged = this.activeEvent?.id !== response.data.event?.id;

                    this.activeEvent = response.data.event;

                    if (eventChanged) {
                        console.log('Event changed, updating video src');
                        this.updateVideoSrc();
                    }
                } catch (exception) {
                    console.error(exception);
                } finally {
                    this.loading = false;
                }
            },

            async startLivestream() {
                this.loading = true;

                try {
                    await this.$http.post(`/api/player/ceremonies/${this.ceremony.id}/livestream/start`);

                    await this.loadStreamingStatus();
                } catch (exception) {
                    console.error(exception);
                } finally {
                    this.loading = false;
                }
            },


            async stopLivestream(withMessage = false) {
                this.loading = true;

                try {
                    await this.$http.post(`/api/player/ceremonies/${this.ceremony.id}/livestream/stop`, {
                        with_message: withMessage,
                    });

                    await this.loadStreamingStatus();
                } catch (exception) {
                    console.error(exception);
                } finally {
                    this.loading = false;
                }
            },

            updateVideoSrc() {
                const eventUrls = this.activeEvent?.streaming_live || [];

                if (eventUrls.length <= 0) {
                    this.videoSrc = null;

                    return;
                }

                // Skip updating the videoSrc when we are already watching the current stream, no need to randomize the HLS when we are already watching
                if (this.videoSrc && eventUrls.includes(this.videoSrc)) {
                    return;
                }

                this.videoSrc = eventUrls[Math.floor(Math.random() * eventUrls.length)].hls;
            },
        },

        beforeDestroy() {
            clearInterval(this.streamingStatusIntervalId);
        },
    };

</script>
